import {
  AgxTenantedPropertyDetails,
  AgxBuyersAgentSearch,
  AgxChattels,
  AgxRuralParticulars,
  AgxFileUpload,
  ElementType,
  AgxSolicitorDetails,
  AgxMarketingConfirmation,
  AgxMarketingLaunchDetails,
  DisplayElement,
  TextInput,
  AlternateAddressSearch,
  SolicitorSearchElement,
  RadioElement,
  Dropdown,
  AgxPropertyAddressForm,
  PropertyAttributesDisplayElement,
  AgreementDetails as AgreementDetailsDisplayElement,
  CheckBoxDisplayElement,
  MarketingPackagesDisplayElement,
  CommissionDisplayElement,
  TenantedPropertyElement,
  ReviewAndSign as ReviewAndSignElement,
  AddressSearchElement,
  TypedDocumentUpload as TypedDocumentUploadElement,
  POADocumentUpload as POADocumentUploadElement,
  MarketingConfirmation,
  CoolingOffPeriodDisplayElement,
  ChattelsIncludedType,
  TextAreaDisplayElement,
  YesNoDisplayElement,
  ConditionalTextInput,
  DepositDetailsDisplayElement,
  SettlementDateDisplayElement,
  MarketingContributionsDisplayElement,
  PriceOpinionElement,
  PriceSpecificationDisplayElement,
  DisplayPriceDisplayElement,
  FileUpload,
  DocumentTypes,
  SellersDisclosurePreparedByElement,
  AgxBodyCorporate,
} from '@urbanx/agx-ui-components';
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import { updateFormValue } from 'features/form/formReducer';
import AgreementDetails from 'features/elements/AgreementDetails/AgreementDetails';
import Commission from 'features/elements/Commission/Commission';
import ComparativeMarketAnalysis from 'features/elements/ComparativeMarketAnalysis/ComparativeMarketAnalysis';
import ListingAgentSelector from 'features/elements/ListingAgentSelector/ListingAgentSelector';
import MarketingPackages from 'features/elements/MarketingPackages/MarketingPackages';
import PoolSafetyCertificate from 'features/elements/PoolSafetyCertificate/PoolSafetyCertificate';
import ReviewAndSign from 'features/elements/ReviewAndSign/ReviewAndSign';
import TypedDocumentUpload from 'features/elements/TypedDocumentUpload/TypedDocumentUpload';
import POADocumentUpload from 'features/elements/POADocumentUpload/POADocumentUpload';
import SaleDetails from 'features/elements/SaleDetails/SaleDetails';
import AdditionalDocumentations from 'features/elements/AdditionalDocumentations/AdditionalDocumentations';
import SendForDocuSigning from 'features/elements/SendForDocuSigning/SendForDocuSigning';
import MarketingContributions from 'features/elements/MarketingContributions/MarketingContributions';
import PriceOpinion from 'features/elements/PriceOpinion/PriceOpinion';
import PriceSpecification from 'features/elements/PriceSpecification/PriceSpecification';
import DisplayPrice from 'features/elements/DisplayPrice/DisplayPrice';
import AdjustmentCommission from 'features/elements/AdjustmentCommission/AdjustmentCommission';
import VendorDetailsDisplay from 'features/elements/VendorDetailsDisplay/VendorDetailsDisplay';
import CommissionSplit from 'features/elements/CommissionSplit/CommissionSplit';
import ExternalConjunction from 'features/elements/ExternalConjunction/ExternalConjunction';
import LegalDescription from 'features/elements/LegalDescription/LegalDescription';
import {
  deleteFile,
  requestGetFileLink,
  useUploadFile,
} from 'components/ui-components/File/fileApi';
import { useFormRegion } from './useFormRegion';
import { getExistingValue } from 'features/form/formReducer';
import useSuburbSuggestions from './useSuburbSuggestions';
import { useFormSaver } from './useFormSaver';
import useFormAddressSelector from 'selectors/useFormAddressSelector';
import ContractReviewAndSign from 'features/elements/ContractReviewAndSign/ContractReviewAndSign';
import Image from 'features/elements/Image/Image';
import SellersDisclosure from 'features/elements/SellersDisclosure/SellersDisclosure';
import { useContext } from 'react';
import { AuthContext } from '@urbanx/agx.auth.client';
import { FormType, SpecialConditionData, VendorBuyerDetails } from '@urbanx/agx.campaigns.client';
import {
  AgxDatePicker,
  AgxDropdown,
  AgxCheckbox,
  AgxSpecialConditionsPicker,
  AgxLaunchDateDetails,
  AgxTextInput,
  AgxTextArea,
  AgxPropertyAttributes,
  AgxRadioGroup,
  AgxYesNoQuestion,
  AgxAlternateAddressSearch,
  AgxBodyCorporateRecord,
  AgxBuildingAndPest,
  AgxContractDeposit,
  AgxDeposit,
  AgxDepositPayable,
  AgxFinanceCondition,
  AgxSettlementDate,
  AgxVendorBuyers,
  AgxCoolingOffPeriod,
  AgxSignboardRemoval,
  AgxConditionalTextInput,
} from '@urbanx/agx.ui-components2';
import { SpecialConditionPicker } from 'types/DisplayElements';
import { parseStringToNumber } from 'utils/numbers';
import { useSelectedCampaignVendors } from './useSelectedCampaignVendors';

interface UseElementRendererProps {
  goToFirstPage: () => void;
  goToPreviousPage: () => void;
  clearValidation: () => void;
}

export const useElementRenderer = (formFunctions: UseElementRendererProps) => {
  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { goToFirstPage, goToPreviousPage, clearValidation } = formFunctions;
  const { suburbSuggestions, getSuburbSuggestions } = useSuburbSuggestions();

  const { campaignId, formValues } = useAppSelector((state) => state.form);
  const { agentId } = useAppSelector((state) => state.agentInfo);
  const { selectedForm } = useAppSelector((state) => state.form);
  const formRegion = useFormRegion();
  const uploadFile = useUploadFile();
  const saveForm = useFormSaver();

  const selectedCampaignVendors = useSelectedCampaignVendors();

  const leadAgentId = formValues?.ListingAgents?.leadAgentId ?? agentId ?? '';

  const specialConditions: SpecialConditionData[] = formValues?.SpecialConditions ?? [];

  const propertyAddress = useFormAddressSelector();
  const australianState = propertyAddress?.state;

  const renderElement = (element: DisplayElement, validate: boolean, parentPathIds: string[]) => {
    const {
      id,
      elementType,
      placeholder,
      subDescription,
      label,
      subLabel,
      required = false,
      isReadonly,
      disabled,
      value: defaultValue,
    } = element;

    const onValueChanged = async (
      value: { id: string; value?: any },
      saveChanges: boolean = false
    ) => {
      clearValidation();
      dispatch(updateFormValue({ ...value, parentPathIds }));

      if (saveChanges) await saveForm(selectedForm?.formConfigType);
    };

    const pathIds = [...(parentPathIds ?? []), id];
    const fullId = pathIds.join('.');

    const existingValue = getExistingValue(pathIds, formValues);

    switch (elementType) {
      case ElementType.TextInput:
        const { inputType } = element as TextInput;
        const textInputType =
          inputType === 'Currency' ? 'currency' : inputType === 'Email' ? 'email' : 'text';
        return (
          <AgxTextInput
            id={id}
            key={fullId}
            label={label}
            placeholder={placeholder}
            required={required}
            onInputValueChange={onValueChanged}
            readonly={isReadonly}
            disabled={disabled}
            stretch
            type={textInputType}
            defaultValue={existingValue ?? defaultValue}
            hintText={subDescription}
          />
        );

      case ElementType.AlternateAddressSearch:
        const { existingAddressLabel, alternateAddressLabel, isSearchByRegion } =
          element as AlternateAddressSearch;

        return (
          <AgxAlternateAddressSearch
            id={id}
            key={fullId}
            label={label ?? ''}
            comparisonAddressLabel={existingAddressLabel}
            alternateAddressLabel={alternateAddressLabel ?? ''}
            onValueChanged={onValueChanged}
            comparisonAddress={formValues.PropertyAddress}
            defaultValue={existingValue ?? defaultValue}
            subDescription={subDescription}
            stateToSearch={isSearchByRegion ? formRegion : false}
            validate={validate}
            required={required}
            placeHolderText="Search..."
            disableStateCountry
          />
        );
      case ElementType.SolicitorSearch:
        const { solicitorLabel } = element as SolicitorSearchElement;

        return (
          <AgxSolicitorDetails
            id={id}
            key={fullId}
            onValueUpdated={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            label={label ?? ''}
            solicitorLabel={solicitorLabel}
            validate={validate}
            required={required}
          />
        );

      case ElementType.BuyersAgentSearch:
        return (
          <AgxBuyersAgentSearch
            id={id}
            key={fullId}
            onValueUpdated={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            label={label ?? ''}
            validate={validate}
          />
        );
      case ElementType.ListingAgentSelector:
        return (
          <ListingAgentSelector
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
          />
        );
      case ElementType.Radio:
        const { options: radioElementOptions } = element as RadioElement;
        return (
          <AgxRadioGroup
            id={id}
            key={fullId}
            label={label ?? ''}
            layout={'vertical'}
            options={radioElementOptions}
            value={existingValue ?? defaultValue}
            validate={validate}
            required={required}
            onChange={(value: string) => onValueChanged({ value, id })}
          />
        );

      case ElementType.Dropdown:
        const { options: dropdownOptions } = element as Dropdown;

        return (
          <AgxDropdown
            id={id}
            key={fullId}
            options={dropdownOptions}
            selected={existingValue ?? defaultValue}
            label={label}
            hideOptionalText={required}
            type="single"
            required={required}
            onChange={(value) => onValueChanged({ id: element.id, value })}
          />
        );

      case ElementType.DateTime:
      case ElementType.Date:
        return (
          <AgxDatePicker
            id={id}
            key={fullId}
            label={label}
            stretch
            value={existingValue ?? defaultValue}
            onChange={(value) => onValueChanged({ id, value })}
            validate={validate}
            required={required}
          />
        );

      case ElementType.LegalDescription:
        return (
          <LegalDescription
            id={id}
            key={fullId}
            label={label}
            readonly={isReadonly}
            legalDescription={existingValue ?? defaultValue}
            onChange={onValueChanged}
            required={required}
            validate={validate}
          />
        );

      case ElementType.PropertyAttributes:
        const { hidePropertyAttributes, isLandAreaRequired } =
          element as PropertyAttributesDisplayElement;

        return (
          <AgxPropertyAttributes
            id={id}
            key={fullId}
            label={element.label}
            defaultValue={existingValue ?? defaultValue}
            hidePropertyAttributes={hidePropertyAttributes}
            onChange={onValueChanged}
            required={required}
            validate={validate}
            isLandAreaRequired={isLandAreaRequired}
          />
        );

      case ElementType.AgreementDetails:
        const { days, restrictDuration } = element as AgreementDetailsDisplayElement;
        return (
          <AgreementDetails
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
            state={australianState}
            days={days}
            restrictDuration={restrictDuration}
          />
        );

      case ElementType.Checkbox:
        const { errorMessage } = element as CheckBoxDisplayElement;
        return (
          <AgxCheckbox
            id={id}
            key={fullId}
            label={label}
            type="checkbox"
            labelSize={'medium'}
            onChange={(value) => onValueChanged({ id, value }, true)}
            checked={existingValue ?? defaultValue}
            validate={validate}
            required={required}
            errorText={
              validate && required && !(existingValue ?? defaultValue) ? errorMessage : undefined
            }
            subLabel={subLabel}
          />
        );

      case ElementType.MarketingPackage:
        const { hideNoMarketing } = element as MarketingPackagesDisplayElement;
        return (
          <MarketingPackages
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
            required={required}
            hideNoMarketing={hideNoMarketing}
          />
        );

      case ElementType.Commission:
        const { commissionTypes, showCommissionRangeDisclaimer } =
          element as CommissionDisplayElement;

        return (
          <Commission
            id={id}
            key={fullId}
            commissionTypes={commissionTypes}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            showCommissionRangeDisclaimer={showCommissionRangeDisclaimer}
            validate={validate}
            required={required}
          />
        );

      case ElementType.ComparativeMarketAnalysis:
        return (
          <ComparativeMarketAnalysis
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
          />
        );

      case ElementType.PoolSafetyCertificate:
        return (
          <PoolSafetyCertificate
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            required={required}
            validate={validate}
          />
        );

      case ElementType.TenantedProperty:
        const {
          noDefaultValueCheck,
          showHolidayRental,
          showPreviousTenancyDetails,
          isManagingAgentNameRequired,
          isManagingAgentEmailRequired,
          lastRentIncreaseRequired,
        } = element as TenantedPropertyElement;
        return (
          <AgxTenantedPropertyDetails
            id={id}
            key={fullId}
            onValueUpdated={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            label={label ?? ''}
            noDefaultValueCheck={noDefaultValueCheck}
            required={required}
            validate={validate}
            showHolidayRentalOption={showHolidayRental}
            showPreviousTenancyDetails={showPreviousTenancyDetails ?? false}
            lastRentIncreaseRequired={lastRentIncreaseRequired}
            isManagingAgentNameRequired={isManagingAgentNameRequired}
            isManagingAgentEmailRequired={isManagingAgentEmailRequired}
          />
        );

      case ElementType.ReviewAndSign:
        const {
          agentSignsFirst,
          formType,
          emailStyle,
          vendors,
          formName,
          sendViaDocuSign,
          disableFormSubmission,
          sendConfigurations,
        } = element as ReviewAndSignElement;

        if (
          formType === FormType.ContractRequest ||
          formType === FormType.MarketingPackageGenerator ||
          formType === FormType.ContractExchange
        ) {
          return (
            <ContractReviewAndSign
              key={fullId}
              goToFirstPage={goToFirstPage}
              goToPreviousPage={goToPreviousPage}
              vendors={vendors}
              formName={formName}
              sendViaDocuSign={sendViaDocuSign}
              disableFormSubmission={disableFormSubmission}
              sendConfigurations={sendConfigurations}
            />
          );
        }

        return (
          <ReviewAndSign
            key={fullId}
            goToFirstPage={goToFirstPage}
            agentSignsFirst={agentSignsFirst}
            emailStyle={emailStyle}
          />
        );

      case ElementType.AddressSearch:
        const { hideUnitLot, stateToSearch } = element as AddressSearchElement;
        return (
          <AgxPropertyAddressForm
            id={id}
            key={fullId}
            label={label}
            searchSuburb
            hideUnitLot={hideUnitLot}
            isReadonly={isReadonly}
            validate={validate}
            showSearchForAddress={false}
            suburbSuggestions={suburbSuggestions}
            stateToSearch={stateToSearch ? stateToSearch : false}
            onFetchSuburbSuggestions={(suburbTerm) => getSuburbSuggestions(suburbTerm)}
            onClearSuburbSuggestions={() => getSuburbSuggestions('')}
            defaultValue={existingValue ?? defaultValue}
            onAddressSelected={(value) => onValueChanged({ id, value })}
            propertyAddressField={false}
          />
        );

      case ElementType.TypedDocumentUpload:
        const { documentConfig } = element as TypedDocumentUploadElement;

        return (
          <TypedDocumentUpload
            id={id}
            key={fullId}
            title={label}
            defaultValue={existingValue ?? defaultValue}
            documentConfig={documentConfig}
            onValueChanged={(value) => {
              onValueChanged(value, true);
            }}
            validate={validate}
          />
        );

      case ElementType.POADocumentUpload:
        const { poaDocumentConfig } = element as POADocumentUploadElement;

        return (
          <POADocumentUpload
            id={id}
            key={fullId}
            title={label ?? ''}
            defaultValue={existingValue ?? defaultValue}
            poaDocumentConfig={poaDocumentConfig}
            onValueChanged={(value) => {
              onValueChanged(value, true);
            }}
            required={required}
            validate={validate}
          />
        );

      case ElementType.FileUpload: {
        const { documentType, multiple, extensions, isSortable } = element as FileUpload;

        return (
          <AgxFileUpload
            id={element.id}
            key={`${element.id}`}
            title={element.label ?? ''}
            fileData={existingValue ?? defaultValue}
            campaignId={campaignId ?? ''}
            documentType={documentType ?? DocumentTypes.Miscellaneous}
            onValueChanged={(value) => {
              onValueChanged(value, true);
            }}
            multiple={multiple ?? false}
            isSortable={isSortable ?? false}
            extensions={extensions ?? []}
            getAuthToken={getAuthToken}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={requestGetFileLink}
            readonly={element.isReadonly ?? false}
            required={required}
            validate={validate}
            subDescription={subDescription}
          />
        );
      }

      case ElementType.VendorBuyerDetails:
        const {
          editScope,
          vendorBuyerConfig,
          powerOfAttorneyConfig,
          companyConfig,
          vendorBuyerTypes,
          stakeholderType,
          companyVendorBuyerTypes,
          exportedIdentityVerificationSourceIds,
        } = element as VendorBuyerDetails;

        return (
          <AgxVendorBuyers
            id={id}
            key={fullId}
            onChange={onValueChanged}
            propertyAddress={propertyAddress}
            defaultValue={existingValue ?? defaultValue}
            editScope={editScope}
            stakeholderType={stakeholderType}
            vendorBuyerTypes={vendorBuyerTypes}
            companyVendorBuyerTypes={companyVendorBuyerTypes}
            vendorBuyerConfig={vendorBuyerConfig}
            companyConfig={companyConfig}
            powerOfAttorneyConfig={powerOfAttorneyConfig}
            validate={validate}
            required={required}
            leadAgentId={leadAgentId}
            campaignId={campaignId ?? ''}
            exportedIdentityVerificationSourceIds={exportedIdentityVerificationSourceIds ?? []}
            renderPowerOfAttorneyDocumentationUpload={({ id, value, onChange }) => (
              <AgxFileUpload
                id={id}
                fileData={value}
                campaignId={campaignId ?? ''}
                documentType={DocumentTypes.PowerOfAttorney}
                onValueChanged={({ value }) => onChange(value ? value[0] : undefined)}
                title="P.O.A Documentation"
                multiple={false}
                getAuthToken={getAuthToken}
                extensions={['pdf']}
                uploadFile={uploadFile}
                deleteFile={deleteFile}
                getFileLink={requestGetFileLink}
              />
            )}
          />
        );

      case ElementType.SaleDetails:
        return (
          <SaleDetails
            id={id}
            key={fullId}
            validate={validate}
            propertyAddress={propertyAddress}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
          />
        );

      case ElementType.LaunchDetails:
        return (
          <AgxLaunchDateDetails
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
          />
        );

      case ElementType.MarketingLaunchDetails:
        return (
          <AgxMarketingLaunchDetails
            id={id}
            key={fullId}
            validate={validate}
            onValueChanged={onValueChanged}
            launchDetails={existingValue ?? defaultValue}
          />
        );

      case ElementType.TextArea:
        const { flexibleHeight: textAreaFlexibleHeight } = element as TextAreaDisplayElement;

        return (
          <AgxTextArea
            id={id}
            key={fullId}
            defaultValue={existingValue ?? defaultValue}
            label={label}
            placeholder={placeholder}
            onInputValueChange={onValueChanged}
            stretch
            rows={3}
            flexibleHeight={textAreaFlexibleHeight}
          />
        );

      case ElementType.YesNoQuestion:
        const { yesLabel, noLabel, reverseLabelOrder, showOptionalLabel } =
          element as YesNoDisplayElement;

        return (
          <AgxYesNoQuestion
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            label={label ?? ''}
            subDescription={subDescription}
            noLabel={noLabel}
            showOptionalLabel={showOptionalLabel}
            yesLabel={yesLabel}
            required={required}
            validate={validate}
            reverseLabelOrder={reverseLabelOrder}
          />
        );

      case ElementType.SecondaryAgentCommissionSplit:
        return (
          <CommissionSplit
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            label={label ?? ''}
            validate={validate}
          />
        );

      case ElementType.SpecialConditions:
        const {
          displayPredefinedConditions,
          label: customConditionLabel,
          subDescription: customConditionSubDescription,
        } = element as SpecialConditionPicker;

        return (
          <div className="revertFormMarginContainer" key={fullId}>
            <AgxSpecialConditionsPicker
              id={id}
              key={fullId}
              selectedSpecialConditions={existingValue ?? defaultValue}
              onValueChanged={onValueChanged}
              campaignId={campaignId ?? ''}
              displayPredefinedConditions={displayPredefinedConditions}
              customConditionLabel={customConditionLabel}
              customConditionSubDescription={customConditionSubDescription}
              validate={validate}
              vendors={selectedCampaignVendors}
            />
          </div>
        );

      case ElementType.DepositPayable:
        return (
          <AgxDepositPayable
            key={fullId}
            id={id}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            required={required}
            validate={validate}
            campaignId={campaignId ?? ''}
            specialConditions={specialConditions}
            salePrice={formValues?.SalePrice ?? 0}
          />
        );

      case ElementType.Deposit:
        const { showDepositDropdownOption } = element as DepositDetailsDisplayElement;

        return (
          <AgxDeposit
            key={fullId}
            id={id}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            showDepositDropdownOption={showDepositDropdownOption}
            required={required}
            validate={validate}
            salePrice={formValues?.SalePrice ?? 0}
          />
        );

      case ElementType.ContractDeposit:
        return (
          <AgxContractDeposit
            key={fullId}
            id={id}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
            specialConditions={specialConditions}
            campaignId={campaignId ?? ''}
            salePrice={formValues?.SalePrice ?? 0}
          />
        );

      case ElementType.BuildingAndPest:
        return (
          <AgxBuildingAndPest
            key={fullId}
            id={id}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
            specialConditions={specialConditions}
            campaignId={campaignId ?? ''}
          />
        );

      case ElementType.SettlementDate:
        const {
          showSpecialConditionOption,
          showAsPerContractOption,
          showInLineWithOption,
          showFromContractDateDropdownOption,
          showPayableOptions,
        } = element as SettlementDateDisplayElement;
        return (
          <AgxSettlementDate
            key={fullId}
            id={id}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            required={required}
            validate={validate}
            showSpecialConditionOption={showSpecialConditionOption}
            showAsPerContractOption={showAsPerContractOption}
            showInLineWithOption={showInLineWithOption}
            showFromContractDateDropdownOption={showFromContractDateDropdownOption}
            showPayableOptions={showPayableOptions}
            specialConditions={specialConditions}
            campaignId={campaignId ?? ''}
          />
        );

      case ElementType.SignboardRemoval:
        return (
          <AgxSignboardRemoval
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
          />
        );
      case ElementType.AdditionalDocumentations:
        return (
          <AdditionalDocumentations
            key={fullId}
            id={id}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
          />
        );

      case ElementType.SendForDocuSigning:
        return (
          <SendForDocuSigning
            key={fullId}
            id={id}
            label={label}
            defaultValue={existingValue ?? defaultValue}
            onValueChanged={onValueChanged}
            validate={validate}
          />
        );

      case ElementType.MarketingContributions:
        const { displayContributionsInfo } = element as MarketingContributionsDisplayElement;

        return (
          <MarketingContributions
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            displayContributionsInfo={displayContributionsInfo}
            label={label ?? ''}
          />
        );

      case ElementType.PriceSpecification:
        const {
          showToBeConfirmed: priceSpecificationShowToBeConfirmed,
          maxPriceRangePercent: priceSpecificationMaxPriceRangePercent,
          defaultUpperRangeSelected: priceSpecificationDefaultUpperRangeSelected,
        } = element as PriceSpecificationDisplayElement;

        return (
          <PriceSpecification
            id={id}
            key={fullId}
            subDescription={subDescription}
            onValueChanged={onValueChanged}
            label={label ?? ''}
            required={required}
            defaultValue={existingValue ?? defaultValue}
            showToBeConfirmed={priceSpecificationShowToBeConfirmed}
            maxPriceRangePercent={priceSpecificationMaxPriceRangePercent}
            defaultUpperRangeSelected={priceSpecificationDefaultUpperRangeSelected}
            validate={validate}
          />
        );

      case ElementType.PriceOpinion:
        const { maxPriceRangePercent: priceOpinionMaxPriceRangePercent } =
          element as PriceOpinionElement;

        return (
          <PriceOpinion
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            label={label}
            defaultValue={existingValue ?? defaultValue}
            maxPriceRangePercent={priceOpinionMaxPriceRangePercent}
          />
        );

      case ElementType.DisplayPrice:
        const { showToBeConfirmed, showReservePrice, nonRuralAgreementSubLabel } =
          element as DisplayPriceDisplayElement;

        return (
          <DisplayPrice
            id={id}
            key={fullId}
            subDescription={subDescription}
            onValueChanged={onValueChanged}
            label={label ?? ''}
            subLabel={subLabel}
            nonRuralAgreementSubLabel={nonRuralAgreementSubLabel}
            required={required}
            defaultValue={existingValue ?? defaultValue}
            showToBeConfirmed={showToBeConfirmed}
            showReservePrice={showReservePrice}
            validate={validate}
          />
        );

      case ElementType.Currency:
        return (
          <AgxTextInput
            id={id}
            key={fullId}
            type="currency"
            label={label}
            placeholder={placeholder}
            onChange={(value) => {
              const parsed = parseStringToNumber(value) ?? '';
              onValueChanged({ id, value: parsed });
            }}
            readonly={isReadonly}
            disabled={disabled}
            stretch
            hideOptionalText
            addTrailingZeros={false}
            defaultValue={existingValue ?? defaultValue}
            errorText={
              validate && required && !existingValue && !defaultValue
                ? `Enter ${label ?? 'value'}`
                : undefined
            }
          />
        );

      case ElementType.SubjectToFinance:
        return (
          <AgxFinanceCondition
            id={id}
            key={fullId}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
            specialConditions={specialConditions}
            campaignId={campaignId ?? ''}
          />
        );

      case ElementType.AdjustmentToCommission:
        return (
          <AdjustmentCommission
            key={fullId}
            id={id}
            label={label}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
            required={required}
          />
        );

      case ElementType.VendorDetailsDisplay:
        return <VendorDetailsDisplay key={fullId} defaultValue={defaultValue} />;

      case ElementType.ExternalConjunction:
        return (
          <ExternalConjunction
            key={fullId}
            id={id}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
          />
        );

      case ElementType.ConditionalTextInput:
        const {
          inputType: conditionalTextInputType,
          noLabel: conditionalTextInputNoLabel,
          yesLabel: conditionalTextInputYesLabel,
          reverseLabelOrder: conditionalTextInputReverseLabelOrder,
          maxLength: conditionalTextInputMaxLength,
          minLength: conditionalTextInputMinLength,
          showTextInputOnYesSelection,
          noTextInputValue,
          inputHeader: conditionalTextInputInputHeader,
          flexibleHeight: conditionalTextInputFlexibleHeight,
        } = element as ConditionalTextInput;

        return (
          <AgxConditionalTextInput
            id={id}
            key={fullId}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            label={label ?? ''}
            subDescription={subDescription}
            placeholder={placeholder}
            disabled={disabled}
            readonly={isReadonly}
            required={required}
            validate={validate}
            inputType={conditionalTextInputType}
            noLabel={conditionalTextInputNoLabel}
            yesLabel={conditionalTextInputYesLabel}
            reverseLabelOrder={conditionalTextInputReverseLabelOrder}
            maxLength={conditionalTextInputMaxLength}
            minLength={conditionalTextInputMinLength}
            showTextInputOnYesSelection={showTextInputOnYesSelection}
            noTextInputValue={noTextInputValue}
            inputHeader={conditionalTextInputInputHeader}
            flexibleHeight={conditionalTextInputFlexibleHeight}
          />
        );

      case ElementType.ChattelsIncluded:
        const { flexibleHeight: chattelsflexibleHeight } = element as ChattelsIncludedType;
        return (
          <AgxChattels
            id={id}
            key={fullId}
            onValueChange={onValueChanged}
            label={label ?? ''}
            state={australianState ?? formRegion ?? ''}
            defaultValue={existingValue ?? defaultValue}
            flexibleHeight={chattelsflexibleHeight}
            validate={validate}
          />
        );
      case ElementType.RuralParticulars:
        return (
          <AgxRuralParticulars
            id={id}
            key={id}
            onValueChange={onValueChanged}
            label={label ?? ''}
            defaultValue={existingValue ?? defaultValue}
          />
        );
      case ElementType.CoolingOffPeriod:
        const { options: coolingOffPeriodOptions } = element as CoolingOffPeriodDisplayElement;
        return (
          <AgxCoolingOffPeriod
            id={id}
            key={id}
            onValueChanged={onValueChanged}
            label={label ?? ''}
            defaultValue={existingValue ?? defaultValue}
            options={coolingOffPeriodOptions}
            required={required}
            validate={validate}
          />
        );
      case ElementType.Image:
        return (
          <Image
            id={id}
            key={id}
            label={label ?? ''}
            defaultValue={existingValue ?? defaultValue}
          />
        );
      case ElementType.MarketingConfirmation:
        const {
          disableNoMarketing,
          disableSignedMarketingPackage,
          editableMarketingValues,
          hideNoMarketingRadioOption,
        } = element as MarketingConfirmation;

        return (
          <AgxMarketingConfirmation
            id={id}
            key={id}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            campaignId={campaignId ?? ''}
            disableNoMarketing={disableNoMarketing}
            disableSignedMarketingPackage={disableSignedMarketingPackage}
            editableMarketingValues={editableMarketingValues}
            hideNoMarketingRadioOption={hideNoMarketingRadioOption}
            validate={validate}
            getAuthToken={getAuthToken}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={requestGetFileLink}
          />
        );

      case ElementType.SellersDisclosurePreparedBy:
        const { requiredSearches } = element as SellersDisclosurePreparedByElement;
        return (
          <SellersDisclosure
            id={id}
            key={id}
            label={label}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            requiredSearches={requiredSearches}
            required={required}
            validate={validate}
          />
        );

      case ElementType.BodyCorporate:
        return (
          <AgxBodyCorporate
            id={id}
            key={id}
            label={label}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            campaignId={campaignId ?? ''}
            getAuthToken={getAuthToken}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={requestGetFileLink}
            required={required}
            validate={validate}
          />
        );

      case ElementType.BodyCorporateRecords:
        return (
          <AgxBodyCorporateRecord
            id={id}
            key={id}
            label={label ?? ''}
            onValueChanged={onValueChanged}
            defaultValue={existingValue ?? defaultValue}
            validate={validate}
            required={required}
            specialConditions={specialConditions}
            campaignId={campaignId ?? ''}
          />
        );

      default:
        console.warn('unknown elementType: ' + elementType);
        return;
    }
  };

  const renderElements = (elements: DisplayElement[], validate: boolean, parentPathIds = []) => {
    return elements.map((element) => renderElement(element, validate, parentPathIds));
  };

  return renderElements;
};
